// WP-C2 — Generate DPR inside Work Progress. Scope + attendance previews are
// SERVER-derived and read-only; this UI edits only updates/plans/blockers/
// photos on Drafts. Submitted DPRs are frozen (revise creates a new revision).
import { useCallback, useEffect, useState } from "react";
import { usePager, Paginator } from "@/components/Paginator";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { compressImage } from "@/lib/imageCompress";
import { SearchableSelect } from "@/components/SearchableSelect";
import DprBlockerMasterModal from "@/components/DprBlockerMasterModal";
import { X, Plus, Trash2, FileText, Download, RefreshCw, Camera, Image as ImageIcon, AlertTriangle, ClipboardList, Users, Sun, CalendarDays, History, Mail, OctagonAlert } from "lucide-react";

const inputCls = "border border-slate-300 rounded px-2 py-1.5 text-xs w-full";
const lineCls = "border border-slate-300 rounded px-2.5 py-2 text-sm w-full";
const today = () => new Date().toLocaleDateString("en-CA");
const q = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const SECTION_COLORS = {
  sky: "bg-sky-50 border-sky-600 text-sky-900",
  teal: "bg-teal-50 border-teal-600 text-teal-900",
  emerald: "bg-emerald-50 border-emerald-600 text-emerald-900",
  violet: "bg-violet-50 border-violet-600 text-violet-900",
  rose: "bg-rose-50 border-rose-600 text-rose-900",
  amber: "bg-amber-50 border-amber-600 text-amber-900",
  navy: "bg-slate-100 border-[#0B1F3A] text-[#0B1F3A]",
};

function SectionHeader({ color, icon: Icon, note, children }) {
  return (
    <div className={`flex items-center gap-2 border-l-4 rounded-r px-3 py-2 text-sm font-extrabold uppercase tracking-wide ${SECTION_COLORS[color]}`}>
      {Icon && <Icon size={16} className="shrink-0" />}
      <span>{children}</span>
      {note && <span className="text-[10px] font-semibold normal-case tracking-normal opacity-70">{note}</span>}
    </div>
  );
}

function LinesEditor({ label, lines, setLines, testid, readOnly }) {
  const set = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  return (
    <div className="space-y-1" data-testid={testid}>
      {lines.map((l, i) => (
        <div key={i} className="flex gap-1 items-start">
          <span className="text-xs font-bold text-slate-400 mt-2.5 w-5">{i + 1}.</span>
          <textarea className={`${lineCls} h-10 flex-[3]`} maxLength={500} placeholder={`${label} line…`}
            value={l.text} disabled={readOnly} onChange={(e) => set(i, "text", e.target.value)}
            data-testid={`${testid}-text-${i}`} />
          <input className={`${lineCls} flex-1`} maxLength={200} placeholder="location / area (optional)"
            value={l.location || ""} disabled={readOnly} onChange={(e) => set(i, "location", e.target.value)}
            data-testid={`${testid}-loc-${i}`} />
          {!readOnly && (
            <button onClick={() => setLines(lines.filter((_, j) => j !== i))}
              className="text-slate-400 hover:text-red-600 mt-2" data-testid={`${testid}-del-${i}`}><Trash2 size={13} /></button>
          )}
        </div>
      ))}
      {!readOnly && lines.length < 10 && (
        <button data-testid={`${testid}-add`} onClick={() => setLines([...lines, { text: "", location: "" }])}
          className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-800 hover:underline">
          <Plus size={12} /> Add line ({lines.length}/10)</button>
      )}
    </div>
  );
}

function ScopeTable({ rows }) {
  if (!rows?.length) return <div className="border border-slate-200 bg-slate-50/60 rounded py-5 text-center text-xs text-slate-400" data-testid="dpr-scope-empty">No BOQ scope progress recorded for this date.</div>;
  return (
    <div className="border border-slate-200 rounded overflow-x-auto">
      <table className="w-full min-w-[760px] text-xs" data-testid="dpr-scope-table">
        <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
          <tr><th className="px-2 py-1.5 text-left">Code</th><th className="px-2 py-1.5 text-left">BOQ Ver</th><th className="px-2 py-1.5 text-left">Item</th>
            <th className="px-2 py-1.5 text-left">UOM</th><th className="px-2 py-1.5 text-right">BOQ Qty</th>
            <th className="px-2 py-1.5 text-right">Prev Cum</th><th className="px-2 py-1.5 text-right">Today</th>
            <th className="px-2 py-1.5 text-right">New Cum</th><th className="px-2 py-1.5 text-right">Prog %</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.boq_line_id} className="border-t border-slate-100">
              <td className="px-2 py-1.5 font-mono font-bold">{r.element_code}</td>
              <td className="px-2 py-1.5 text-slate-500" data-testid={`dpr-scope-ver-${r.boq_line_id}`}>{r.boq_version_number == null ? "—" : `V${r.boq_version_number}`}</td>
              <td className="px-2 py-1.5 max-w-[220px] truncate" title={r.name}>{r.name}</td>
              <td className="px-2 py-1.5">{r.uom_name}</td>
              <td className="px-2 py-1.5 text-right">{q(r.boq_quantity)}</td>
              <td className="px-2 py-1.5 text-right">{q(r.previous_cumulative_qty)}</td>
              <td className="px-2 py-1.5 text-right font-bold text-emerald-800">{q(r.qty_today)}</td>
              <td className="px-2 py-1.5 text-right">{q(r.new_cumulative_qty)}</td>
              <td className={`px-2 py-1.5 text-right font-bold ${r.progress_pct > 100 ? "text-amber-700" : ""}`}>{r.progress_pct == null ? "—" : `${r.progress_pct}%`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AttendanceBox({ att }) {
  if (!att?.recorded) return (
    <div className="border border-amber-300 bg-amber-50 rounded p-3 text-xs text-amber-800 font-bold" data-testid="dpr-att-missing">
      <AlertTriangle size={13} className="inline mr-1 -mt-0.5" /> Labour attendance not recorded for this date — the PDF will state this explicitly.
    </div>
  );
  return (
    <div className="border border-slate-200 rounded overflow-x-auto">
      <table className="w-full min-w-[640px] text-xs" data-testid="dpr-att-table">
        <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
          <tr><th className="px-3 py-1.5 text-left">Skill Category</th><th className="px-3 py-1.5 text-right">Present</th>
            <th className="px-3 py-1.5 text-right">Half Day</th><th className="px-3 py-1.5 text-right">Effective</th></tr>
        </thead>
        <tbody>
          {att.skill_summary.map((s) => (
            <tr key={s.skill_category} className="border-t border-slate-100">
              <td className="px-3 py-1.5">{s.skill_category}</td>
              <td className="px-3 py-1.5 text-right">{s.present}</td>
              <td className="px-3 py-1.5 text-right">{s.half_day}</td>
              <td className="px-3 py-1.5 text-right font-bold">{s.effective_count}</td>
            </tr>
          ))}
          <tr className="border-t border-slate-200 bg-slate-50 font-bold">
            <td className="px-3 py-1.5">Total Effective (Sheet Rev {att.revision})</td><td /><td />
            <td className="px-3 py-1.5 text-right text-emerald-800" data-testid="dpr-att-total">{att.total_effective}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function PhotoStrip({ project, dpr, editable, onChanged }) {
  const [busy, setBusy] = useState(false);
  // Jun-2026 (user request) — multi-select upload, capped at 6 photos total.
  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const room = 6 - (dpr.photos || []).length;
    if (files.length > room)
      toast(`Only ${room} more photo${room === 1 ? "" : "s"} allowed (max 6) — uploading the first ${room}`);
    setBusy(true);
    let ok = 0, dup = 0;
    for (const f of files.slice(0, room)) {
      try {
        const small = await compressImage(f);
        const fd = new FormData();
        fd.append("file", small);
        const r = await api.post(`/projects/${project.id}/dpr/${dpr.id}/photos`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        if (r.data.duplicate) dup += 1; else ok += 1;
      } catch (e) { toast.error(`${f.name}: ${e?.response?.data?.detail || "upload failed"}`); }
    }
    setBusy(false);
    if (ok || dup)
      toast.success(`${ok} photo${ok === 1 ? "" : "s"} added${dup ? ` · ${dup} duplicate${dup === 1 ? "" : "s"} skipped` : ""}`);
    onChanged();
  };
  const remove = async (p) => {
    try { await api.delete(`/projects/${project.id}/dpr/${dpr.id}/photos/${p.id}`); toast.success("Photo removed"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };
  const setCaption = async (p, caption) => {
    try { await api.put(`/projects/${project.id}/dpr/${dpr.id}`, { photo_meta: (dpr.photos || []).map((x) => ({ id: x.id, caption: x.id === p.id ? caption : x.caption })) }); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Caption save failed"); }
  };
  return (
    <div className="space-y-2" data-testid="dpr-photos">
      <div className="flex flex-wrap gap-2">
        {(dpr.photos || []).map((p, i) => (
          <div key={p.id} className="border border-slate-200 rounded p-1.5 w-40 space-y-1" data-testid={`dpr-photo-${i}`}>
            <DprPhotoImg project={project} dpr={dpr} photo={p} />
            <input className="border border-slate-200 rounded px-1 py-0.5 text-[10px] w-full" placeholder="caption…"
              defaultValue={p.caption || ""} disabled={!editable}
              onBlur={(e) => e.target.value !== (p.caption || "") && setCaption(p, e.target.value)}
              data-testid={`dpr-photo-caption-${i}`} />
            <div className="flex items-center justify-between text-[9px] text-slate-400">
              <span>{Math.round((p.size || 0) / 1024)} KB</span>
              {editable && <button onClick={() => remove(p)} className="text-slate-400 hover:text-red-600" data-testid={`dpr-photo-del-${i}`}><Trash2 size={11} /></button>}
            </div>
          </div>
        ))}
      </div>
      {editable && (dpr.photos || []).length < 6 && (
        <div className="flex items-center gap-3 text-[11px] font-bold text-emerald-800">
          <label className="cursor-pointer hover:underline inline-flex items-center gap-1">
            <ImageIcon size={13} /> Add from gallery (multi-select)
            <input data-testid="dpr-photo-gallery" type="file" accept="image/jpeg,image/png,image/jpg" multiple className="hidden"
              onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
          </label>
          <label className="cursor-pointer hover:underline inline-flex items-center gap-1">
            <Camera size={13} /> Capture photo
            <input data-testid="dpr-photo-camera" type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
          </label>
          <span className="font-normal text-slate-400">{(dpr.photos || []).length}/6 · select up to {6 - (dpr.photos || []).length} at once · JPG/PNG · ≤500 KB after compression{busy ? " · uploading…" : ""}</span>
        </div>
      )}
    </div>
  );
}

function DprPhotoImg({ project, dpr, photo }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/projects/${project.id}/dpr/${dpr.id}/photos/${photo.id}`, { responseType: "blob" })
      .then((r) => alive && setUrl(URL.createObjectURL(r.data))).catch(() => {});
    return () => { alive = false; };
  }, [project.id, dpr.id, photo.id]);
  return url ? <img src={url} alt={photo.caption || photo.filename} className="w-full h-24 object-cover rounded" />
    : <div className="w-full h-24 bg-slate-100 rounded animate-pulse" />;
}

function MailConfigModal({ project, cfg, onClose, onSaved }) {
  const canManage = !!cfg?.can_manage;
  const [enabled, setEnabled] = useState(!!cfg?.enabled);
  const [rows, setRows] = useState(cfg?.recipients?.length ? cfg.recipients : [{ name: "", email: "" }]);
  const [busy, setBusy] = useState(false);
  const set = (i, k, v) => setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/projects/${project.id}/dpr-mail-config`, {
        enabled, recipients: rows.filter((x) => (x.email || "").trim()) });
      toast.success("DPR mail configuration saved");
      onSaved(r.data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-12" data-testid="dpr-mail-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 text-xs">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <div className="text-sm font-bold text-slate-900 inline-flex items-center gap-2"><Mail size={15} className="text-sky-600" /> DPR Mail Configuration</div>
            <div className="text-[10px] text-slate-500">The DPR PDF is emailed automatically to this list on every Submit / Revision · list is specific to {project.project_code || "this project"}</div>
          </div>
          <button data-testid="dpr-mail-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          {!canManage && (
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded px-3 py-1.5" data-testid="dpr-mail-readonly-note">
              Read-only — you need the "DPR Mail Config" permission to change these settings.
            </div>
          )}
          <label className="flex items-center gap-2 font-bold text-slate-700 text-sm cursor-pointer">
            <input type="checkbox" data-testid="dpr-mail-enabled" checked={enabled} disabled={!canManage}
              onChange={(e) => setEnabled(e.target.checked)} className="w-4 h-4" />
            Send DPR mail automatically (On/Off)
          </label>
          <div className="space-y-1.5">
            <div className="text-[10px] uppercase font-bold text-slate-500">Recipients (max 20)</div>
            {rows.map((r, i) => (
              <div key={i} className="flex gap-1.5 items-center">
                <input data-testid={`dpr-mail-name-${i}`} className={inputCls} placeholder="Name" value={r.name || ""}
                  disabled={!canManage} onChange={(e) => set(i, "name", e.target.value)} />
                <input data-testid={`dpr-mail-email-${i}`} className={inputCls} placeholder="email@example.com" value={r.email || ""}
                  disabled={!canManage} onChange={(e) => set(i, "email", e.target.value)} />
                {canManage && (
                  <button data-testid={`dpr-mail-remove-${i}`} onClick={() => setRows((x) => x.filter((_, j) => j !== i))}
                    className="text-rose-600 hover:text-rose-800 shrink-0"><Trash2 size={13} /></button>
                )}
              </div>
            ))}
            {canManage && rows.length < 20 && (
              <button data-testid="dpr-mail-add" onClick={() => setRows((x) => [...x, { name: "", email: "" }])}
                className="inline-flex items-center gap-1 text-sky-700 font-bold hover:underline"><Plus size={12} /> Add Recipient</button>
            )}
          </div>
          <div className="text-[10px] text-slate-400">Mails are sent using the company SMTP settings (Administration → Email Configuration). If SMTP is disabled globally, DPR mails are skipped and logged.</div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded font-bold text-slate-600">Close</button>
            {canManage && (
              <button data-testid="dpr-mail-save" disabled={busy} onClick={save}
                className="px-3 py-1.5 bg-sky-700 text-white font-bold rounded inline-flex items-center gap-1.5 disabled:opacity-50">
                <Mail size={12} /> Save Configuration
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DprTab({ project }) {
  const { hasPerm } = useAuth();
  const canCreate = hasPerm("project_dpr", "create");
  const canEditDraft = hasPerm("project_dpr", "edit_draft");
  const canSubmit = hasPerm("project_dpr", "submit");
  const canRevise = hasPerm("project_dpr", "revise");
  const canDownload = hasPerm("project_dpr", "download");
  const [date, setDate] = useState(today());
  const [prev, setPrev] = useState(null);
  const [masters, setMasters] = useState([]);
  const [canManageBlockers, setCanManageBlockers] = useState(false);
  const [blockerMasterOpen, setBlockerMasterOpen] = useState(false);
  const [updates, setUpdates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [noPlanReason, setNoPlanReason] = useState("");
  const [noBlocker, setNoBlocker] = useState(false);
  const [blockers, setBlockers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [hist, setHist] = useState([]);
  const [mailCfg, setMailCfg] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);
  const histPager = usePager(hist, 10);

  const dpr = prev?.dpr || null;
  const isDraft = dpr?.status === "Draft";
  const isPending = dpr?.status === "Pending PDF";
  const isSubmitted = dpr?.status === "Submitted";
  const scopeSig = (rows) => JSON.stringify((rows || []).map((r) => [r.boq_line_id, r.qty_today]).sort());
  const scopeDrift = isSubmitted && !!prev && scopeSig(prev.scope_rows) !== scopeSig(dpr?.scope_rows);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${project.id}/dpr/preview`, { params: { dpr_date: date } });
      setPrev(r.data);
      const d = r.data.dpr;
      setUpdates(d?.updates?.length ? d.updates : []);
      setPlans(d?.plans?.length ? d.plans : []);
      setNoPlanReason(d?.no_plan_reason || "");
      setNoBlocker(!!d?.no_blocker);
      setBlockers(d?.blockers?.length ? d.blockers : []);
    } catch (e) { setPrev(null); toast.error(e?.response?.data?.detail || "Failed to load DPR preview"); }
  }, [project.id, date]);
  // Refreshes photos/scope only — never resets unsaved text (updates/plans/blockers).
  const refreshPhotos = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${project.id}/dpr/preview`, { params: { dpr_date: date } });
      setPrev(r.data);
    } catch { /* keep current view */ }
  }, [project.id, date]);
  const loadHist = useCallback(async () => {
    try { const r = await api.get(`/projects/${project.id}/dpr`, { params: { limit: 100 } }); setHist(r.data.rows || []); } catch { /* noop */ }
  }, [project.id]);
  useEffect(() => { load(); loadHist(); }, [load, loadHist]);
  const loadMasters = useCallback(() => {
    api.get("/dpr-blocker-masters").then((r) => {
      setMasters(r.data.rows || []);
      setCanManageBlockers(!!r.data.can_manage);
    }).catch(() => {});
  }, []);
  useEffect(() => { loadMasters(); }, [loadMasters]);
  useEffect(() => {
    api.get(`/projects/${project.id}/dpr-mail-config`).then((r) => setMailCfg(r.data)).catch(() => setMailCfg(null));
  }, [project.id]);

  const createDraft = async () => {
    setBusy(true);
    try { await api.post(`/projects/${project.id}/dpr`, { dpr_date: date }); toast.success("DPR draft created"); load(); loadHist(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Draft creation failed"); }
    finally { setBusy(false); }
  };
  const saveDraft = async (silent) => {
    try {
      await api.put(`/projects/${project.id}/dpr/${dpr.id}`, {
        updates, plans, no_plan_reason: noPlanReason, no_blocker: noBlocker,
        blockers: blockers.map((b) => ({ blocker_id: b.blocker_id, detail: b.detail || "" })) });
      if (!silent) toast.success("Draft saved");
      load();
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); return false; }
  };
  const submit = async () => {
    setBusy(true);
    try {
      if (!(await saveDraft(true))) { setBusy(false); setConfirm(false); return; }
      const r = await api.post(`/projects/${project.id}/dpr/${dpr.id}/submit`);
      toast.success(`DPR ${r.data.dpr_number} submitted — PDF generated`);
      setConfirm(false); load(); loadHist();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); setConfirm(false); load(); }
    finally { setBusy(false); }
  };
  const resume = async () => {
    setBusy(true);
    try { const r = await api.post(`/projects/${project.id}/dpr/${dpr.id}/resume-pdf`); toast.success(`PDF generated — ${r.data.dpr_number}`); load(); loadHist(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Resume failed"); }
    finally { setBusy(false); }
  };
  const revise = async () => {
    const reason = window.prompt("Reason for revising this submitted DPR:");
    if (reason === null) return;
    try { await api.post(`/projects/${project.id}/dpr/${dpr.id}/revise`, { reason }); toast.success("Revision draft created"); load(); loadHist(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Revise failed"); }
  };
  const download = async (row) => {
    try {
      const r = await api.get(`/projects/${project.id}/dpr/${row.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(row.dpr_number || "DPR").replaceAll("/", "_")}_rev${row.revision}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed (permission required)"); }
  };

  const editable = isDraft && canEditDraft;
  const backdated = date < today();

  return (
    <div className="space-y-3" data-testid="dpr-tab">
      <div className="flex items-center gap-3 flex-wrap text-xs">
        <label className="flex items-center gap-2 font-bold text-slate-600">DPR Date
          <input data-testid="dpr-date-input" type="date" max={today()} className="border border-slate-300 rounded px-2 py-1.5"
            value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        {/* Jun-2026 — badge reflects the user's ACTUAL backdate access: a
            role WITH project_dpr.backdate sees a green "allowed" note
            instead of the scary "requires permission" warning. */}
        {backdated && (prev?.can_backdate
          ? <span className="text-emerald-800 bg-emerald-50 border border-emerald-300 rounded px-2 py-1 text-[11px]" data-testid="dpr-backdate-note">Backdated — allowed for your role</span>
          : <span className="text-amber-800 bg-amber-50 border border-amber-300 rounded px-2 py-1 text-[11px]" data-testid="dpr-backdate-note">Backdated — requires DPR backdate permission{prev?.can_backdate === false ? " (you don't have it)" : ""}</span>)}
        {dpr && (
          <span data-testid="dpr-status-badge" className={`px-2 py-1 rounded text-[11px] font-bold border ${isSubmitted ? "bg-emerald-50 border-emerald-300 text-emerald-800" : isPending ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-slate-50 border-slate-300 text-slate-700"}`}>
            {dpr.dpr_number ? `${dpr.dpr_number} · ` : ""}{isPending ? "Submission Pending PDF" : dpr.status} · Rev {dpr.revision}
          </span>
        )}
        {dpr?.source_updated_after_generation && (
          <span className="text-red-700 bg-red-50 border border-red-300 rounded px-2 py-1 text-[11px] font-bold" data-testid="dpr-stale-badge">
            <AlertTriangle size={11} className="inline -mt-0.5 mr-1" />Source data updated after generation ({[...new Set((dpr.source_changes || []).map((c) => c.source))].join(", ")})
          </span>
        )}
        {mailCfg && (
          <button data-testid="dpr-mail-btn" onClick={() => setMailOpen(true)}
            className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded border font-bold text-[11px] transition-colors ${mailCfg.enabled
              ? "bg-sky-50 border-sky-300 text-sky-800 hover:bg-sky-100"
              : "bg-slate-50 border-slate-300 text-slate-600 hover:bg-slate-100"}`}>
            <Mail size={12} /> DPR Mail
            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-black ${mailCfg.enabled ? "bg-emerald-600 text-white" : "bg-slate-400 text-white"}`} data-testid="dpr-mail-state">
              {mailCfg.enabled ? "ON" : "OFF"}
            </span>
          </button>
        )}
        {canManageBlockers && (
          <button data-testid="dpr-blocker-master-btn" onClick={() => setBlockerMasterOpen(true)}
            className={`${mailCfg ? "" : "ml-auto "}inline-flex items-center gap-1.5 px-3 py-1.5 rounded border font-bold text-[11px] bg-orange-50 border-orange-300 text-orange-800 hover:bg-orange-100 transition-colors`}
            title="Manage the DPR blocker list (add / edit / deactivate)">
            <OctagonAlert size={12} /> Manage Blockers
          </button>
        )}
      </div>

      {prev && !dpr && (
        <div className="border border-slate-200 bg-slate-50/60 rounded p-4 text-xs flex items-center justify-between flex-wrap gap-2">
          <span className="text-slate-500">No DPR exists for {date}.</span>
          {canCreate && <button data-testid="dpr-create-btn" disabled={busy} onClick={createDraft}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-700 text-white font-bold rounded disabled:opacity-50"><FileText size={13} /> Create DPR Draft</button>}
        </div>
      )}

      {isPending && (
        <div className="border border-amber-300 bg-amber-50 rounded p-3 text-xs text-amber-800 flex items-center justify-between flex-wrap gap-2" data-testid="dpr-pending-banner">
          <span className="font-bold">Submission is frozen but the PDF hasn't been generated yet (Submission Pending PDF).</span>
          {canSubmit && <button data-testid="dpr-resume-btn" disabled={busy} onClick={resume}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-700 text-white font-bold rounded disabled:opacity-50"><RefreshCw size={12} /> Resume PDF Generation</button>}
        </div>
      )}

      {prev && (
        <>
          <SectionHeader color="sky" icon={ClipboardList} note="(auto — from Work Progress; read-only)">Today's Scope Update</SectionHeader>
          {isDraft && (
            <div className="text-[11px] text-sky-800 bg-sky-50 border border-sky-200 rounded px-2 py-1.5" data-testid="dpr-scope-live-note">
              Live scope — auto-refreshed from Work Progress (including any BOQ revision); it is frozen into the DPR only when you Submit.
            </div>
          )}
          {scopeDrift && (
            <div className="text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-300 rounded px-2 py-1.5" data-testid="dpr-scope-drift-note">
              <AlertTriangle size={11} className="inline -mt-0.5 mr-1" />
              Work Progress scope has changed since this DPR was submitted — click Revise to capture the latest scope in a new revision.
            </div>
          )}
          <ScopeTable rows={isSubmitted || isPending ? dpr.scope_rows : prev.scope_rows} />
          <SectionHeader color="teal" icon={Users} note="(auto — from Labour Attendance; skill totals only)">Labour Attendance Summary</SectionHeader>
          <AttendanceBox att={isSubmitted || isPending ? dpr.attendance : prev.attendance} />
        </>
      )}

      {dpr && (isDraft || isSubmitted || isPending) && (
        <>
          <SectionHeader color="emerald" icon={Sun} note="* (max 10 lines)">Today's Update</SectionHeader>
          <div className="border-l-4 border-emerald-200 pl-3">
            <LinesEditor label="Update" lines={updates} setLines={setUpdates} testid="dpr-updates" readOnly={!editable} />
          </div>
          <SectionHeader color="violet" icon={CalendarDays} note="(max 10 lines)">Tomorrow's Plan</SectionHeader>
          <div className="border-l-4 border-violet-200 pl-3">
            <LinesEditor label="Plan" lines={plans} setLines={setPlans} testid="dpr-plans" readOnly={!editable} />
          </div>
          {plans.length === 0 && (
            <label className="block text-xs space-y-1">
              <span className="font-bold text-amber-700">No work planned — mandatory reason *</span>
              <input data-testid="dpr-noplan-reason" className={inputCls} value={noPlanReason} disabled={!editable}
                onChange={(e) => setNoPlanReason(e.target.value)} maxLength={500} />
            </label>
          )}
          <SectionHeader color="rose" icon={AlertTriangle} note="(max 10)">Blockers</SectionHeader>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" data-testid="dpr-no-blocker" checked={noBlocker} disabled={!editable}
              onChange={(e) => { setNoBlocker(e.target.checked); if (e.target.checked) setBlockers([]); }} />
            <b>No Blocker</b> (cannot be combined with blockers below)
          </label>
          {!noBlocker && (
            <div className="space-y-1" data-testid="dpr-blockers">
              {blockers.map((b, i) => {
                const bm = masters.find((m) => m.id === b.blocker_id) || {};
                return (
                <div key={i} className="flex gap-1 items-start">
                  <div className="flex-1">
                    <SearchableSelect value={b.blocker_id}
                      onChange={(id) => setBlockers(blockers.map((x, j) => (j === i ? { ...x, blocker_id: id, name: (masters.find((m) => m.id === id) || {}).name } : x)))}
                      items={masters.filter((m) => m.active !== false)}
                      getLabel={(m) => `${m.name}${m.code ? ` (${m.code})` : ""}${m.scope ? ` · ${m.scope}` : ""}`}
                      getSearchText={(m) => `${m.name} ${m.code} ${m.scope || ""}`}
                      placeholder="— select blocker —" searchPlaceholder="Search blockers…"
                      testid={`dpr-blocker-select-${i}`} disabled={!editable} />
                  </div>
                  {b.blocker_id && (
                    <span data-testid={`dpr-blocker-scope-${i}`} title="Scope (from Blocker Master)"
                      className={`mt-2 shrink-0 inline-block px-1.5 py-0.5 text-[9px] uppercase font-bold border rounded ${
                        bm.scope === "Client" ? "bg-sky-50 text-sky-700 border-sky-300"
                        : bm.scope === "Radiants" ? "bg-orange-50 text-orange-700 border-orange-300"
                        : bm.scope ? "bg-slate-100 text-slate-600 border-slate-300" : "bg-white text-slate-300 border-slate-200"}`}>
                      {bm.scope || "—"}
                    </span>
                  )}
                  <input className={`${inputCls} flex-[2]`} maxLength={500} placeholder={`details${bm.name === "Other" ? " (mandatory for Other)" : " (optional)"}`}
                    value={b.detail || ""} disabled={!editable}
                    onChange={(e) => setBlockers(blockers.map((x, j) => (j === i ? { ...x, detail: e.target.value } : x)))}
                    data-testid={`dpr-blocker-detail-${i}`} />
                  {editable && <button onClick={() => setBlockers(blockers.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600 mt-2"><Trash2 size={13} /></button>}
                </div>
                );
              })}
              {editable && blockers.length < 10 && (
                <button data-testid="dpr-blocker-add" onClick={() => setBlockers([...blockers, { blocker_id: "", detail: "" }])}
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-800 hover:underline"><Plus size={12} /> Add blocker</button>
              )}
            </div>
          )}
          <SectionHeader color="amber" icon={Camera} note="(max 6)">Site Photographs</SectionHeader>
          <PhotoStrip project={project} dpr={dpr} editable={editable} onChanged={refreshPhotos} />

          <div className="flex items-center justify-end gap-2 pt-1">
            {isSubmitted && canDownload && (
              <button data-testid="dpr-download-btn" onClick={() => download(dpr)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-emerald-800 border border-emerald-300 rounded hover:bg-emerald-50"><Download size={13} /> Download PDF</button>
            )}
            {isSubmitted && dpr.is_current && canRevise && (
              <button data-testid="dpr-revise-btn" onClick={revise}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-amber-800 border border-amber-300 rounded hover:bg-amber-50">Revise DPR</button>
            )}
            {editable && (
              <>
                <button data-testid="dpr-save-btn" disabled={busy} onClick={() => saveDraft(false)}
                  className="px-3 py-1.5 text-xs font-bold border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50">Save Draft</button>
                {canSubmit && (
                  <button data-testid="dpr-submit-btn" disabled={busy} onClick={() => setConfirm(true)}
                    className="px-4 py-1.5 bg-emerald-700 text-white text-xs font-bold rounded disabled:opacity-50">Submit DPR</button>
                )}
              </>
            )}
          </div>
        </>
      )}

      <div className="pt-2">
        <SectionHeader color="navy" icon={History}>DPR History</SectionHeader>
      </div>
      <div className="border border-slate-200 rounded overflow-x-auto">
        <table className="w-full min-w-[680px] text-xs" data-testid="dpr-history-table">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">DPR Number</th>
              <th className="px-3 py-2 text-left">Rev</th><th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Submitted By</th><th className="px-3 py-2 text-left">Source</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {hist.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400" data-testid="dpr-history-empty">No DPRs yet.</td></tr>}
            {histPager.slice.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`dpr-hist-${r.dpr_date}-${r.revision}`}>
                <td className="px-3 py-2 font-mono font-bold">{r.dpr_date}</td>
                <td className="px-3 py-2 font-mono">{r.dpr_number || "—"}</td>
                <td className="px-3 py-2">Rev {r.revision}{r.is_current ? <span className="ml-1 text-[9px] font-bold text-emerald-700 uppercase">current</span> : ""}</td>
                <td className="px-3 py-2">{r.status === "Pending PDF" ? "Pending PDF" : r.status}{r.source_updated_after_generation ? <span className="ml-1 text-[9px] font-bold text-red-600 uppercase" title="Source data updated after generation">stale</span> : ""}</td>
                <td className="px-3 py-2">{r.submitted_by || r.created_by}</td>
                <td className="px-3 py-2 text-[10px] text-slate-400">{[...new Set((r.source_changes || []).map((c) => c.source))].join(", ") || "—"}</td>
                <td className="px-3 py-2 text-right">
                  {r.pdf?.stored_as && canDownload && (
                    <button data-testid={`dpr-hist-dl-${r.dpr_date}-${r.revision}`} onClick={() => download(r)}
                      className="text-emerald-800 hover:underline inline-flex items-center gap-1"><Download size={12} /> PDF</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Paginator pager={histPager} testid="dpr-history-pager" />
      </div>

      {confirm && dpr && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10" data-testid="dpr-confirm-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div className="text-sm font-bold text-slate-900">Submit DPR — {date}</div>
              <button onClick={() => setConfirm(false)} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-2 text-xs">
              <div className="text-slate-600">Submitting freezes this DPR and generates the immutable PDF. Confirm the contents:</div>
              <ul className="space-y-1 text-slate-700">
                <li data-testid="dpr-confirm-scope">• Scope lines included: <b>{(prev?.scope_rows || []).length}</b></li>
                <li data-testid="dpr-confirm-att">• Attendance: <b>{prev?.attendance?.recorded ? `Sheet Rev ${prev.attendance.revision} · effective ${prev.attendance.total_effective}` : "NOT RECORDED (will be stated in the PDF)"}</b></li>
                <li>• Today's Update lines: <b>{updates.filter((u) => u.text?.trim()).length}</b></li>
                <li>• Tomorrow's Plan lines: <b>{plans.filter((p) => p.text?.trim()).length}</b>{plans.length === 0 ? ` (No work planned — ${noPlanReason || "reason required"})` : ""}</li>
                <li>• Blockers: <b>{noBlocker ? "No Blocker" : blockers.filter((b) => b.blocker_id).length}</b></li>
                <li>• Photographs: <b>{(dpr.photos || []).length}</b></li>
              </ul>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setConfirm(false)} className="px-3 py-1.5 border rounded">Cancel</button>
                <button data-testid="dpr-confirm-submit" disabled={busy} onClick={submit}
                  className="px-4 py-1.5 bg-emerald-700 text-white font-bold rounded disabled:opacity-50">{busy ? "Submitting…" : "Confirm & Submit"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {mailOpen && mailCfg && (
        <MailConfigModal project={project} cfg={mailCfg}
          onClose={() => setMailOpen(false)} onSaved={setMailCfg} />
      )}

      {blockerMasterOpen && (
        <DprBlockerMasterModal onClose={() => { setBlockerMasterOpen(false); loadMasters(); }} />
      )}
    </div>
  );
}
